var _ = require('underscore');
var TorsoView = require('backbone-torso/modules/View');
var TorsoEvents = require('backbone-torso/modules/Events');
var demographicsRelatedPersonInfoTemplate = require('./demographicsRelatedPersonInfoTemplate.hbs');
var handlebars = require('hbsfy/runtime');
var DemographicsValidationBehavior = require('./../behaviors/DemographicsValidationBehavior');

var SECTION_NAMES = {
  MAIL_INFO: 'mail_info',
  CONTACT_INFO: 'contact_info',
  NEXT_OF_KIN: 'next_of_kin',
  NEXT_OF_KIN_2: 'next_of_kin2',
  EMERGENCY_CONTACT: 'emergency_contact',
  EMERGENCY_CONTACT_2: 'emergency_contact2'
};

/**
 * View for displaying a related person information for patients demographics
 *
 * @class DemographicsRelatedPersonInfoView
 * @constructor
 */
var DemographicsRelatedPersonInfoView = TorsoView.extend({
  tagName: 'section',
  className: 'content-block',
  template: demographicsRelatedPersonInfoTemplate,

  behaviors: {
    validationBehavior: {
      behavior: DemographicsValidationBehavior
    }
  },

  /**
   * @method initialize
   * @override
   * @param options {Object}
   *   @param options.dataModel {Object} the data of person information
   *   @param options.config {Object} the configuration for each properties
   *   @param options.viewName {String} name of the view
   *   @param options.hasIdImage {boolean} whether the demographics model contains id image or not
   *   @param options.isInPreReg {boolean} whether it's in pre-reg or not
   *   @param options.editUrl {String} the url for corresponding edit view out of pre-reg
   *   @param options.preRegEditUrl {String} the url for corresponding edit view in pre-reg
   */
  initialize: function(options) {
    this._demographicsModel = options.dataModel || this._demographicsModel;
    this._dataProperty = options.dataProperty || this._dataProperty;
    this._config = options.config || this._config;
    this._viewName = options.viewName || this._viewName;
    this._hasIdImage = options.hasIdImage || this._hasIdImage;
    this._isInPreReg = options.isInPreReg || this._isInPreReg;
    this._editUrl = options.editUrl || this._editUrl;
    this._preRegEditUrl = options.preRegEditUrl || this._preRegEditUrl;

    this.generateContext();
    this.listenTo(this._demographicsModel, 'change:' + this._dataProperty, this.generateContext);
    this.listenTo(this.viewState, 'change:context', this.render);
  },

  /**
   * @method _combineNameOrAddressConfig
   * @param config the configuration for name or address
   * @param contextModel the model in context for name or address
   * @return contextConfig the configuration in context for name or address
   * @private
   */
  _combineNameOrAddressConfig: function(config, contextModel) {
    var contextConfig = {};
    contextConfig.disabled = config.fieldPermissions === 'DISABLED';

    if (!contextConfig.disabled) {
      if (config) {
        var properties = _.keys(config);
        _.each(properties, function (property) {
          var conf = _.propertyOf(config)(property);
          var data = _.propertyOf(contextModel)(property);
          if (conf.fieldPermissions === 'DISABLED') {
            delete contextModel[property];
          } else if ((conf.validationConfiguration && conf.validationConfiguration.required) && !data) {
            contextConfig.invalid = true;
          }
        });
      }
    }
    return contextConfig;
  },

  /**
   * @method _getNameConfig
   * @param config the configuration for name
   * @param contextModel the model in context for name
   * @returns contextConfig the configuration in context for name
   * @private
   */
  _getNameConfig: function(config, contextModel) {
    var contextConfig = {};
    if (config) {
      contextConfig = this._combineNameOrAddressConfig(config, contextModel);
      contextConfig.hasData = !!handlebars.helpers.formatName(contextModel);
      contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    } else {
      contextConfig.disabled = true;
    }

    return contextConfig;
  },

  /**
   * @method _getAddressConfig
   * @param config the configuration for address
   * @param contextModel the model in context for address
   * @returns contextConfig the configuration in context for address
   * @private
   */
  _getAddressConfig: function(config, contextModel) {
    var contextConfig = {};
    if (config) {
      contextConfig = this._combineNameOrAddressConfig(config, contextModel);
      contextConfig.hasData = !!handlebars.helpers.inlineAddress(contextModel);
      contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    } else {
      contextConfig.disabled = true;
    }

    return contextConfig;
  },

  /**
   * @method _combinePhoneOrEmailConfig
   * @param config the configuration for phone or email related fields
   * @return contextConfig the configuration in context for phone or email related fields
   * @private
   */
  _combinePhoneOrEmailConfig: function(config) {
    var contextConfig = {};
    if (config) {
        contextConfig.disabled = (config.fieldPermissions === 'DISABLED');
        if (!contextConfig.disabled) {
          contextConfig.required = config.required && (config.validationConfiguration && config.validationConfiguration.required);
        }
    } else {
      contextConfig.disabled = true;
    }

    return contextConfig;
  },

  /**
   * @method _getPhoneConfig
   * @param config the configuration for phone related fields
   * @param contextModel the model in context for phone related fields
   * @returns contextConfig the configuration in context for phone related fields
   * @private
   */
  _getPhoneConfig: function(config, contextModel) {
    var contextConfig = {};
    if (config) {
      contextModel = contextModel || {};
      contextConfig = this._combinePhoneOrEmailConfig(config);
      contextConfig.invalid = contextConfig.required && !(contextModel.areaCode && contextModel.exchange && contextModel.digits);
      contextConfig.hasData = !!handlebars.helpers.formatPhoneNumber(contextModel);
      contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    } else {
      contextConfig.disabled = true;
    }
    return contextConfig;
  },

  /**
   * @method _getEmailConfig
   * @param config the configuration for email related fields
   * @param contextModel the model in context for email
   * @returns contextConfig the configuration in context for email related fields
   * @private
   */
  _getEmailConfig: function(config, contextModel) {
    var contextConfig = {};
    if (config) {
      contextConfig = this._combinePhoneOrEmailConfig(config);
      contextConfig.hasData = !_.isEmpty(contextModel);
      contextConfig.missing = contextConfig.required && !contextConfig.hasData;
    } else {
      contextConfig.disabled = true;
    }
    return contextConfig;
  },

  /**
   * @method _getRelationshipConfig
   * @param config the configuration for relationship field
   * @param contextModel the model in context for relationship field
   * @returns contextConfig the configuration in context for relationship field
   * @private
   */
  _getRelationshipConfig: function(config, contextModel) {
    var contextConfig = {};
    if (config) {
      contextConfig.disabled = config.fieldPermissions === 'DISABLED';
      if (!contextConfig.disabled) {
        contextConfig.required = (config.validationConfiguration && config.validationConfiguration.required);
        contextConfig.hasData = !_.isEmpty(contextModel);
        contextConfig.missing = contextConfig.required && !contextConfig.hasData;
      }
    } else {
      contextConfig.disabled = true;
    }
    return contextConfig;
  },

  /**
   * @method _getImageConfig
   * @param config the configuration for image field
   * @returns contextConfig the configuration in context for image field
   * @private
   */
  _getImageConfig: function(config) {
    var contextConfig = {};
    if (this._viewName !== SECTION_NAMES.CONTACT_INFO) {
      contextConfig.disabled = true;
    } else {
      if (config) {
        contextConfig.disabled = config.fieldPermissions === 'DISABLED';
      } else {
        contextConfig.disabled = true;
      }
    }
    return contextConfig;
  },

  /**
   * Calculate the context based on data and configuration and set it on the view state
   * @method generateContext
   */
  generateContext: function() {
    var context = TorsoView.prototype.prepare.apply(this);

    context.model = _.clone(this._demographicsModel.get(this._dataProperty));
    context.config = {};

    context.config.image = this._getImageConfig(this._config.image);
    context.config.name = this._getNameConfig(this._config.name, context.model.name);
    if (this._viewName === SECTION_NAMES.CONTACT_INFO) {
      context.config.name.disabled = true;
    }
    context.config.address = this._getAddressConfig(this._config.address, context.model.address);
    var phoneConfig = this._config.phone;
    context.config.homePhone = this._getPhoneConfig(phoneConfig && phoneConfig.homePhone, context.model.homePhone);
    context.config.cellPhone = this._getPhoneConfig(phoneConfig && phoneConfig.cellPhone, context.model.cellPhone);
    context.config.workPhone = this._getPhoneConfig(phoneConfig && phoneConfig.workPhone, context.model.workPhone);
    context.config.otherPhone = this._getPhoneConfig(phoneConfig && phoneConfig.otherPhone, context.model.otherPhone);
    context.config.emailAddress = this._getEmailConfig(this._config.email, context.model.emailAddress);
    context.config.relationship = this._getRelationshipConfig(this._config.relationship, context.model.relationship);

    var hasData = false;
    _.each(context.config, function(config) {
      if (config.hasData) {
        hasData = true;
      }
    });

    context.config.noData = !this.hasRequiredFields() && !hasData;

    context.viewName = this._viewName;
    context.hasIdImage = this._hasIdImage;
    context.editUrl = this._isInPreReg ? this._preRegEditUrl : this._editUrl;
    context.sectionNames = SECTION_NAMES;

    this.set('context', context);
    TorsoEvents.trigger('demographics:validate');
  }
});

DemographicsRelatedPersonInfoView.SECTION_NAMES = SECTION_NAMES;

module.exports = DemographicsRelatedPersonInfoView;
